import { Association } from "@entities";
import { createPhoneNumberFilter, type HyperTableFilter } from "@shared/frontend/hyper-table";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import { TeamsSelect } from "../../../components/teams-select";
import { SkillsSelect } from "../../../components/skills-select/SkillsSelect";
import { Box, Button, Checkbox, Chip, FormControlLabel, ListItemText, MenuItem, Select, TextField } from "@mui/material";
import { FormGoogle } from "../../../components/form-components/form-google-autocomplete/FormGoogle";
import { useState } from "react";
import { BasicButton } from "../../../components/generic-components/button/Button";
import { useOrganization } from "../../../contexts/OrganizationProvider";

const filters: HyperTableFilter<Association>[] = [
    { fieldName: "user.name", id: "name" },
    { fieldName: "user.gender", id: "gender" },
    { fieldName: "user.address", id: "address" },
    {
        id: "teams",
        icon: "literal",
        caption: "פרויקטים",
        renderFilter: ({ onSave, meta }) => {
            const { teams: existTeams } = meta;
            return (
                <TeamsSelect
                    id="teams-filter"
                    onChange={(teams) =>
                        onSave({
                            value: teams.length
                                ? Association.hyperFilter({
                                    teams: {
                                        $some: {
                                            teamId: teams as string[],
                                        },
                                    },
                                })
                                : {},
                            meta: { teams },
                        })
                    }
                    value={existTeams as string[]}
                />
            );
        },
    },
    {
        id: "skills",
        icon: "literal",
        caption: "יכולות",
        renderFilter: ({ onSave, meta }) => {
            const { skills } = meta;
            return (
                <SkillsSelect
                    anchorEl={true}
                    menu={false}
                    showSaveButton={false}
                    onChange={(skills) => {
                        onSave({
                            value: skills.length
                                ? Association.hyperFilter({
                                    user: {
                                        $is: {
                                            skills: {
                                                $some: {
                                                    skillId: { $in: skills },
                                                },
                                            },
                                        },
                                    },
                                })
                                : {},
                            meta: { skills },
                        });
                    }}
                    value={skills as string[]}
                />
            );
        },
    },
    createPhoneNumberFilter(Association, {
        fieldName: "user.phoneNumber",
        id: "phoneNumber",
    }),
    {
        id: "location",
        icon: "string",
        caption: "מרחק מכתובת",
        renderFilter: ({ onSave, meta }) => {
            const filterObj = {
                location: meta?.location || "",
                distanceKm: meta?.distanceKm || 0,
                latitude: meta?.latitude || 0,
                longitude: meta?.longitude || 0,
            };

            const [state, setState] = useState(filterObj);

            return (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", paddingInline: "0.5rem" }}>
                    <FormGoogle
                        label="מיקום"
                        placeValue={state.location as string}
                        dir="rtl"
                        onLocationChange={(location) => {
                            setState({
                                ...state,
                                location: location.locationName,
                                latitude: location.latLng.lat,
                                longitude: location.latLng.lng,
                            });
                        }}
                    />
                    <TextField
                        type="number"
                        label="מרחק בקילומטרים"
                        value={state.distanceKm}
                        onChange={(e) =>
                            setState({
                                ...state,
                                distanceKm: Number(e.target.value) || "",
                            })
                        }
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <BasicButton
                            style={{
                                borderRadius: "10px",
                                backgroundColor: "#05204c",
                                padding: "0.3rem 1rem",
                                fontWeight: 400,
                                fontSize: "small",
                            }}
                            text="שמירה"
                            onClick={() => {
                                onSave({
                                    value: Association.isAddressInRadiusFromPoint({
                                        radius: { distance: Number(state.distanceKm), measure: "km" },
                                        lat: state.latitude as number,
                                        lng: state.longitude as number,
                                    }),
                                    meta: state,
                                });
                            }}
                        />
                    </div>
                </div>
            );
        },
    },
    {
        id: "preferredPositions",
        icon: "string",
        caption: "העדפות",
        renderFilter: ({ onSave, meta }) => {
            const [value, setValue] = useState<string>(meta.value as string || "");

            const handleSave = () => {
                onSave({
                    value: value
                        ? Association.hyperFilter({
                            preferredPositions: { $some: { $contains: value } },
                        })
                        : {},
                    meta: { value },
                });
            };

            return (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", paddingInline: "0.5rem" }}>
                    <TextField
                        style={{ width: "80%", margin: "0 10%" }}
                        label="תפקידים מועדפים"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        inputProps={{
                            style: { direction: "rtl" },
                        }}
                        placeholder="הזן תפקידים מועדפים"
                        fullWidth
                        variant="outlined"
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                        >
                            שמירה
                        </Button>
                    </div>
                </div>
            );
        },
    },
    { fieldName: "user.email", id: "email", caption: "אימייל" },
    { fieldName: "user.numberID", id: "numberID", caption: "מספר זהות" },
    {
        id: "age",
        icon: "string",
        caption: "גיל",
        renderFilter: ({ onSave, meta }) => {
            const [minAge, setMinAge] = useState(String(meta.minAge ?? ""));
            const [maxAge, setMaxAge] = useState(String(meta.maxAge ?? ""));
            const handleSave = () => {
                const conditions = [];
                if (minAge) {
                    conditions.push({
                        user: {
                            $is: {
                                dateOfBirth: {
                                    $lte: new Date(new Date().getFullYear() - Number(minAge), 11, 31),
                                },
                            },
                        },
                    });
                }
                if (maxAge) {
                    conditions.push({
                        user: {
                            $is: {
                                dateOfBirth: {
                                    $gte: new Date(new Date().getFullYear() - Number(maxAge) - 1, 0, 1),
                                },
                            },
                        },
                    });
                }
                onSave({
                    value: conditions.length
                        ? conditions.length === 1
                            ? Association.hyperFilter(conditions[0])
                            : Association.hyperFilter({ $and: conditions })
                        : {},
                    meta: { minAge, maxAge },
                });
            };
            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1, minWidth: 200 }}>
                    <TextField size="small" type="number" label="גיל מינימלי" value={minAge} onChange={(e) => setMinAge(e.target.value)} />
                    <TextField size="small" type="number" label="גיל מקסימלי" value={maxAge} onChange={(e) => setMaxAge(e.target.value)} />
                    <Button variant="contained" size="small" onClick={handleSave}>שמירה</Button>
                </Box>
            );
        },
    },
    {
        id: "hasVehicle",
        icon: "literal",
        caption: "רכב",
        renderFilter: ({ onSave, meta }) => {
            const checked = !!meta.checked;
            return (
                <FormControlLabel
                    sx={{ px: 1 }}
                    control={
                        <Checkbox
                            checked={checked}
                            onChange={(e) => {
                                const next = e.target.checked;
                                onSave({
                                    value: next
                                        ? Association.hyperFilter({ user: { $is: { hasVehicle: true } } })
                                        : {},
                                    meta: { checked: next, text: next ? "עם רכב" : "" },
                                });
                            }}
                        />
                    }
                    label="רק מתנדבים עם רכב"
                />
            );
        },
    },
    {
        id: "activityStatus",
        icon: "literal",
        caption: "סטטוס פעילות",
        renderFilter: ({ onSave, meta }) => {
            const value = (meta.value as string) || "";
            const options = [
                { id: "NEW", label: "חדש" },
                { id: "ACTIVE", label: "פעיל" },
                { id: "INACTIVE", label: "לא פעיל" },
                { id: "LEGACY", label: "מאגר ישן" },
            ];
            return (
                <Box sx={{ p: 1, minWidth: 180 }}>
                    <Select
                        fullWidth
                        size="small"
                        displayEmpty
                        value={value}
                        onChange={(event) => {
                            const next = event.target.value;
                            onSave({
                                value: next
                                    ? Association.hyperFilter({ activityStatus: next as "NEW" | "ACTIVE" | "INACTIVE" | "LEGACY" })
                                    : {},
                                meta: {
                                    value: next,
                                    text: options.find((option) => option.id === next)?.label ?? "",
                                },
                            });
                        }}
                    >
                        <MenuItem value="">הכל</MenuItem>
                        {options.map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </Box>
            );
        },
    },
    {
        id: "preferredCategories",
        icon: "literal",
        caption: "קטגוריות מועדפות",
        renderFilter: ({ onSave, meta }) => {
            const { currOrganization } = useOrganization();
            const selectedCategoryIds = (meta.selectedCategoryIds || []) as string[];
            const categories = (currOrganization?.categories || [])
                .slice()
                .sort(compareBySortOrder);
            const catalogIds = new Set(categories.map((category) => category.id));
            const knownCategoryIds = selectedCategoryIds.filter((id) =>
                catalogIds.has(id),
            );
            return (
                <Select
                    multiple
                    value={knownCategoryIds}
                    onChange={(event) => {
                        const selectedIds = (typeof event.target.value === "string"
                            ? event.target.value.split(",")
                            : event.target.value
                        ).filter((id: string) => catalogIds.has(id));
                        onSave({
                            value: selectedIds.length
                                ? Association.hyperFilter({
                                    user: {
                                        $is: {
                                            categoryPreferences: {
                                                $some: { categoryId: { $in: selectedIds } },
                                            },
                                        },
                                    },
                                })
                                : {},
                            meta: { selectedCategoryIds: selectedIds },
                        });
                    }}
                    renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {(selected as string[]).map((id) => {
                                const name = categories.find((category) => category.id === id)?.name?.trim();
                                if (!name) return null;
                                return <Chip key={id} size="small" label={name} />;
                            })}
                        </Box>
                    )}
                    fullWidth
                    size="small"
                >
                    {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                            <Checkbox checked={knownCategoryIds.includes(category.id)} />
                            <ListItemText primary={category.name} />
                        </MenuItem>
                    ))}
                </Select>
            );
        },
    },
];

export default filters;
